"use client";

import { useState, useMemo } from "react";
import { PageTitleHeader } from "@/components/ui-components/page-title-header";
import { BlogFilterBar } from "@/components/ui-components/blog-filter-bar";
import { BlogGrid } from "@/components/ui-components/blog-grid";
import { BlogPagination } from "@/components/ui-components/blog-pagination";
import type { BlogPostSummary, BlogCategory } from "@/types/blog";
import { useTranslations } from "next-intl";
import { BLOG_POST_META } from "@/lib/constants";

const POSTS_PER_PAGE = 6;

export default function BlogPage() {
  const t = useTranslations('pages.blog');
  const tPosts = useTranslations('blogPosts');
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Build categories from translations so labels update with locale
  const categories: BlogCategory[] = [
    { id: "all", name: t('categories.all'), slug: "all" },
    { id: "maintenance", name: t('categories.maintenance'), slug: "maintenance" },
    { id: "repairs", name: t('categories.repairs'), slug: "repairs" },
    { id: "tips", name: t('categories.tips'), slug: "tips" },
    { id: "customer-stories", name: t('categories.customerStories'), slug: "customer-stories" },
  ];

  // Build translated post summaries by merging stable meta with translated
  // copy, looked up by the post's stable `id` (never array position) so the
  // metadata and message bundle can be reordered independently without
  // pairing the wrong title/excerpt with the wrong post. content is omitted
  // from the type entirely — BlogGrid/BlogPostCard never render the body,
  // and it isn't part of the client message bundle in the first place
  // (see i18n/messages/{locale}/blog-content.json, read server-side only).
  const translatedPosts: BlogPostSummary[] = BLOG_POST_META.flatMap((meta) => {
    // next-intl's raw() does NOT return undefined for a missing message —
    // it returns the message key itself ("blogPosts.<id>") as a fallback
    // string, which is truthy. Check the actual shape (cast to unknown
    // first) rather than casting to an optional-shaped type and trusting
    // truthiness, or a missing translation slips through with
    // title/excerpt/categories all undefined — see lib/blog.ts for the same
    // fix, where this exact mistake caused a 500 on BlogPostCard's
    // post.categories.slice(0, 2).
    const rawTranslated = tPosts.raw(meta.id) as unknown;
    if (
      typeof rawTranslated !== "object" ||
      rawTranslated === null ||
      typeof (rawTranslated as { title?: unknown }).title !== "string"
    ) {
      console.error(`Missing "blogPosts.${meta.id}" translation for slug "${meta.slug}"`);
      return [];
    }
    const translated = rawTranslated as { title: string; excerpt: string; categories: string[] };

    return [{
      ...meta,
      title: translated.title,
      excerpt: translated.excerpt,
      categories: translated.categories,
    }];
  });

  // Filter by stable categorySlug — locale-independent, never breaks on translated labels
  const filteredPosts = useMemo(() => {
    let filtered = translatedPosts;

    if (activeCategory !== "all") {
      filtered = BLOG_POST_META
        .filter((meta) => meta.categorySlug === activeCategory)
        .map((meta) => translatedPosts.find((p) => p.id === meta.id)!);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(query) ||
          post.excerpt.toLowerCase().includes(query) ||
          post.author.name.toLowerCase().includes(query) ||
          post.categories.some((cat) => cat.toLowerCase().includes(query)) ||
          post.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [activeCategory, searchQuery, translatedPosts]);

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const paginatedPosts = filteredPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  return (
    <div className="flex max-w-screen overflow-hidden flex-col min-h-screen bg-site text-white">
      <main className="flex-1 pt-20">
        <PageTitleHeader title={t('title')} subtitle={t('subtitle')} />
        <section className="py-16 bg-site">
          <div className="px-4 md:px-16 lg:px-20">
            <BlogFilterBar
              categories={categories}
              activeCategory={activeCategory}
              searchQuery={searchQuery}
              onCategoryChange={handleCategoryChange}
              onSearchChange={handleSearchChange}
            />

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-white">
                {t('allPosts')}
              </h2>
              <p className="text-gray-400 mt-2">
                {t('showing')} {paginatedPosts.length} {t('of')} {filteredPosts.length} {t('posts')}
              </p>
            </div>

            <BlogGrid posts={paginatedPosts} className="mb-16" />

            {totalPages > 1 && (
              <BlogPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
